import { useState, useEffect, CSSProperties, ReactNode } from 'react';
import outputs from '../../amplify_outputs.json';

const API_BASE: string =
  (import.meta.env.VITE_API_ENDPOINT as string) ||
  (outputs as any).custom?.API_ENDPOINT ||
  '';

// ─── Shared styles ────────────────────────────────────────────────────────────

function Logo() {
  return (
    <div style={{ textAlign: 'center', marginBottom: 32 }}>
      <img
        src="https://realtimerx-static-assets.s3.us-east-1.amazonaws.com/icons/intentflo_facebook_profile_1024x1024.jpg"
        alt="intentFlo"
        style={{
          width: 58, height: 58, borderRadius: 16,
          display: 'block', margin: '0 auto 14px',
          boxShadow: '0 0 40px rgba(59,130,246,0.28)',
          objectFit: 'cover',
        }}
      />
      <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.03em', color: '#e8edf5' }}>
        intent<span style={{ color: '#3b82f6' }}>flo</span>
      </div>
      <div style={{ fontSize: 10, color: '#3b4f6a', fontFamily: 'DM Mono, monospace',
                    letterSpacing: '0.14em', marginTop: 5 }}>
        CLIENT ONBOARDING
      </div>
    </div>
  );
}

function Field({
  label, value, onChange, placeholder, helper, prefix,
}: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; helper?: string; prefix?: string;
}) {
  const [focused, setFocused] = useState(false);
  const inputStyle: CSSProperties = {
    flex: 1, background: 'transparent', color: '#e8edf5',
    border: 'none', padding: '10px 13px', fontSize: 13,
    fontFamily: 'Sora, sans-serif', outline: 'none',
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.03em' }}>
        {label}
      </label>
      <div style={{
        display: 'flex', alignItems: 'center',
        background: '#060a10', border: `1px solid ${focused ? '#3b82f6' : '#1a2840'}`,
        borderRadius: 8,
        boxShadow: focused ? '0 0 0 3px rgba(59,130,246,0.12)' : 'none',
        transition: 'border-color 0.15s, box-shadow 0.15s',
      }}>
        {prefix && (
          <span style={{
            padding: '10px 0 10px 13px', fontSize: 13,
            color: '#475569', fontFamily: 'DM Mono, monospace', userSelect: 'none',
          }}>
            {prefix}
          </span>
        )}
        <input
          value={value}
          placeholder={placeholder}
          style={inputStyle}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={e => onChange(e.target.value)}
        />
      </div>
      {helper && (
        <div style={{ fontSize: 11, color: '#4a5d7a', lineHeight: 1.5 }}>{helper}</div>
      )}
    </div>
  );
}

function Step({ num, title, children }: { num: number; title: string; children: ReactNode }) {
  return (
    <div style={{
      background: '#080c14', border: '1px solid #111927',
      borderRadius: 12, padding: 20, marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          background: 'linear-gradient(135deg,#1d4ed8,#2563eb)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0,
        }}>
          {num}
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#e8edf5' }}>{title}</div>
      </div>
      {children}
    </div>
  );
}

function InlineStep({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'flex-start' }}>
      <div style={{
        width: 20, height: 20, borderRadius: 6,
        background: '#0f1e36', border: '1px solid #1e3a5f',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 10, fontWeight: 700, color: '#60a5fa', flexShrink: 0, marginTop: 1,
      }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

function Link({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{
      color: '#3b82f6', textDecoration: 'underline',
      textDecorationColor: 'rgba(59,130,246,0.4)',
    }}>
      {children}
    </a>
  );
}

// ─── CheckItem ───────────────────────────────────────────────────────────────

function CheckItem({ checked, onChange, optional, who, microSteps, sectionLink, children }: {
  checked: boolean; onChange: () => void; optional?: boolean;
  who?: 'client' | 'us';
  microSteps?: (string | { text: string; link?: { label: string; url: string }; note?: boolean })[];
  sectionLink?: { label: string; url: string };
  children: ReactNode;
}) {
  if (who === 'us') {
    return (
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        padding: '6px 0', borderTop: '1px dashed #101a28',
      }}>
        <div style={{
          width: 18, height: 18, borderRadius: 4, flexShrink: 0, marginTop: 2,
          background: 'rgba(124,58,237,0.12)',
          border: '1.5px solid rgba(124,58,237,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
            <path d="M4.5 1L5.5 3.5H8L5.9 5.3L6.7 8L4.5 6.4L2.3 8L3.1 5.3L1 3.5H3.5Z"
                  fill="#a78bfa"/>
          </svg>
        </div>
        <div style={{ flex: 1 }}>
          <span style={{
            display: 'inline-block', fontSize: 10, fontWeight: 700,
            color: '#a78bfa', background: 'rgba(124,58,237,0.12)',
            borderRadius: 3, padding: '1px 6px', marginRight: 7,
            letterSpacing: '0.04em', verticalAlign: 'middle',
          }}>IntentFlo</span>
          <span style={{ fontSize: 13, color: '#4a5d7a', lineHeight: 1.6 }}>{children}</span>
        </div>
      </div>
    );
  }

  // ── Section mode: step heading + numbered micro-steps ────────────────────
  if (microSteps && microSteps.length > 0) {
    return (
      <div style={{
        borderTop: '1px dashed #101a28', paddingTop: 12, marginTop: 4, paddingBottom: 4,
      }}>
        {/* Section heading row: checkbox + title + optional link button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={onChange}
            style={{
              width: 18, height: 18, borderRadius: 4, flexShrink: 0,
              background: checked ? 'rgba(16,185,129,0.15)' : 'transparent',
              border: `1.5px solid ${checked ? '#10b981' : '#1e3a5f'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', transition: 'all 0.15s', padding: 0,
            }}
          >
            {checked && (
              <svg width="10" height="10" viewBox="0 0 10 10">
                <path d="M1.5 5L3.8 7.5L8.5 2.5" stroke="#10b981" strokeWidth="1.5"
                      fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </button>
          <div style={{
            fontSize: 13, fontWeight: 700, flex: 1,
            color: checked ? '#4a5d7a' : '#e8edf5',
            textDecoration: checked ? 'line-through' : 'none',
            transition: 'color 0.15s',
          }}>
            {children}
            {optional && <span style={{ fontSize: 11, color: '#334155', fontWeight: 400, marginLeft: 6 }}>(optional)</span>}
          </div>
          {sectionLink && (
            <a
              href={sectionLink.url} target="_blank" rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center',
                background: '#0d1f3c', border: '1px solid #1e3a6e',
                borderRadius: 6, padding: '4px 10px',
                fontSize: 11, fontWeight: 600, color: '#60a5fa',
                textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              {sectionLink.label}
            </a>
          )}
        </div>
        {/* Numbered micro-steps */}
        <ol style={{ margin: '0 0 4px 26px', padding: 0 }}>
          {microSteps.map((step, i) => {
            const text = typeof step === 'string' ? step : step.text;
            const isNote = typeof step === 'object' && step.note;
            const link = typeof step === 'object' ? step.link : undefined;
            return (
              <li key={i} style={{
                fontSize: 12, lineHeight: 1.7, marginBottom: 2,
                color: isNote ? '#475569' : '#94a3b8',
                fontStyle: isNote ? 'italic' : 'normal',
                listStyleType: isNote ? 'none' : 'decimal',
                marginLeft: isNote ? -16 : 0,
              }}>
                {text}
                {link && (
                  <a href={link.url} target="_blank" rel="noopener noreferrer"
                     style={{ color: '#3b82f6', marginLeft: 6, fontSize: 11 }}>
                    {link.label} ↗
                  </a>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    );
  }

  // ── Default single-line mode ──────────────────────────────────────────────
  return (
    <button
      type="button"
      onClick={onChange}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        background: 'none', border: 'none', cursor: 'pointer',
        padding: '5px 0', width: '100%', textAlign: 'left',
      }}
    >
      <div style={{
        width: 18, height: 18, borderRadius: 4, flexShrink: 0, marginTop: 2,
        background: checked ? 'rgba(16,185,129,0.15)' : 'transparent',
        border: `1.5px solid ${checked ? '#10b981' : '#1e3a5f'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.15s',
      }}>
        {checked && (
          <svg width="10" height="10" viewBox="0 0 10 10">
            <path d="M1.5 5L3.8 7.5L8.5 2.5" stroke="#10b981" strokeWidth="1.5"
                  fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
      <div style={{ flex: 1 }}>
        <span style={{
          display: 'inline-block', fontSize: 10, fontWeight: 700,
          color: '#60a5fa', background: 'rgba(37,99,235,0.10)',
          borderRadius: 3, padding: '1px 6px', marginRight: 7,
          letterSpacing: '0.04em', verticalAlign: 'middle',
        }}>You</span>
        <span style={{
          fontSize: 13, lineHeight: 1.6,
          color: checked ? '#4a5d7a' : '#94a3b8',
          textDecoration: checked ? 'line-through' : 'none',
          transition: 'color 0.15s',
        }}>
          {children}
          {optional && <span style={{ color: '#334155', fontSize: 11, marginLeft: 6 }}>(optional)</span>}
        </span>
      </div>
    </button>
  );
}

// ─── Platform setup config ────────────────────────────────────────────────────

const PLATFORM_OPTIONS = [
  { id: 'meta',     label: 'Meta Ads',        icon: '📘', desc: 'Facebook & Instagram' },
  { id: 'google',   label: 'Google Ads',      icon: '🔍', desc: 'Search, Display & YouTube' },
  { id: 'linkedin', label: 'LinkedIn Ads',    icon: '💼', desc: 'LinkedIn Campaign Manager' },
  { id: 'tiktok',   label: 'TikTok Ads',      icon: '🎵', desc: 'TikTok for Business' },
  { id: 'twitter',  label: 'X (Twitter) Ads', icon: '✖️', desc: 'X Ads Manager' },
  { id: 'custom',   label: 'Custom / Other',  icon: '⚙️', desc: 'Request a custom integration' },
] as const;

interface PlatformField {
  key: string; label: string; placeholder: string;
  prefix?: string; helper: string; optional?: boolean; multiline?: boolean;
}
interface MetaMicroStep { text: string; link?: { label: string; url: string }; note?: boolean; }
interface PlatformStep {
  label: string; optional?: boolean; who: 'client' | 'us';
  microSteps?: (string | MetaMicroStep)[];
  sectionLink?: { label: string; url: string };
}
interface PlatformSetup { title: string; icon: string; steps: PlatformStep[]; fields: PlatformField[]; }

const PLATFORM_SETUPS: Record<string, PlatformSetup> = {
  meta: {
    title: 'Meta Ads', icon: '📘',
    steps: [
      {
        who: 'client',
        label: 'Step 1 — Open Meta Business',
        sectionLink: { label: 'Open Meta Business ↗', url: 'https://business.facebook.com' },
        microSteps: [
          'Log in with your Facebook profile.',
          { text: 'If you see "Create a business" or "Create a business portfolio" — click it, enter your business name and email, and follow the prompts (~2 minutes).' },
          'If you already see your business name at the top, skip to Step 2.',
          { text: 'Note: Meta may ask you to verify your business with a document (EIN letter, utility bill). This is normal — complete it if prompted.', note: true },
        ],
      },
      {
        who: 'client',
        label: 'Step 2 — Add IntentFlo as a Partner',
        sectionLink: { label: 'Open Partners settings ↗', url: 'https://business.facebook.com/settings/partners/' },
        microSteps: [
          'In the left menu, click the ⚙️ Settings gear.',
          'Under Users, click Partners.',
          'Click the blue Add button in the top right.',
          'Choose "Give a partner access to your assets."',
          'Paste the IntentFlo Partner ID (shown above) and click Next.',
          'Confirm you see "IntentFlo LLC" on screen, then click Done.',
        ],
      },
      {
        who: 'client',
        label: 'Step 3 — Share your Ad Account',
        sectionLink: { label: 'Open Partners settings ↗', url: 'https://business.facebook.com/settings/partners/' },
        microSteps: [
          'In Settings → Users → Partners, click IntentFlo LLC.',
          'Click Share assets (or Assign assets).',
          'On the left, click Ad accounts → check your account.',
          'On the right, turn ON: Manage campaigns (or Advertise) and View performance.',
          'Click Save changes.',
          { text: 'No ad account yet? Settings → Accounts → Ad accounts → Add → "Create a new ad account" → name it, pick time zone + currency → confirm. Then return here.', note: true },
        ],
      },
      {
        who: 'client',
        label: 'Step 4 — Share your Facebook Page',
        sectionLink: { label: 'Open Partners settings ↗', url: 'https://business.facebook.com/settings/partners/' },
        microSteps: [
          'In the same Share assets window, click Pages on the left.',
          'Check the box next to your business Page.',
          'Turn ON: Create ads (or Manage ads) and View Page performance.',
          'Click Save changes.',
        ],
      },
      {
        who: 'client',
        label: 'Step 5 — Share your Pixel or Dataset',
        optional: true,
        sectionLink: { label: 'Open Events Manager ↗', url: 'https://business.facebook.com/events_manager/' },
        microSteps: [
          'In Share assets, click Pixels, Datasets, or Data sources — whichever you see.',
          'Check the box next to your pixel or dataset.',
          'Turn ON: Manage events (or Manage Pixel) and View performance.',
          'Click Save changes.',
          { text: 'Not sure if you have one? Skip this — we\'ll check during setup.', note: true },
        ],
      },
      {
        who: 'client',
        label: 'Step 6 — Add a payment method (required)',
        sectionLink: { label: 'Open Billing ↗', url: 'https://business.facebook.com/billing_hub/accounts/details/' },
        microSteps: [
          'Go to Ads Manager → Billing → Payment settings.',
          'Click Add payment method and add your card.',
          { text: 'Meta bills you directly for ad spend. IntentFlo manages your campaigns but does not pay media costs on your behalf.', note: true },
        ],
      },
      { who: 'us', label: 'Once your access is shared and your form is submitted, we\'ll connect your account, configure your audience sync, and prepare your first campaign — nothing more needed from you.' },
    ],
    fields: [
      { key: 'adAccountId', label: 'Your Meta Ad Account ID', placeholder: '123456789012345', prefix: 'act_', helper: 'Find this in Meta Business Settings → Ad accounts → click your account name. Copy the numbers only — for example if you see "act_123456789" copy just "123456789". Do not include "act_".' },
      { key: 'pixelId', label: 'Your Pixel / Dataset ID (optional)', placeholder: '1234567890', helper: 'Find this in Meta Events Manager → your Pixel or Dataset → Settings tab. Leave blank if you don\'t have one yet — we\'ll set it up for you.', optional: true },
    ],
  },
  google: {
    title: 'Google Ads', icon: '🔍',
    steps: [
      { who: 'client', label: 'Grant access: in Google Ads → wrench icon → Admin → Access and Security → Users → Add user → invite info@intentflo.com → role: Standard or Admin → Send' },
      { who: 'client', label: 'Accept Customer Match terms: Tools & Settings → Billing → Account Settings → scroll to Customer Match → accept the terms of service' },
      { who: 'client', label: 'Find your Customer ID: shown in the top-right corner of Google Ads in the format 123-456-7890 — enter below without dashes' },
      { who: 'us', label: 'Connect your Google Ads account via OAuth, configure Customer Match User Lists in Hightouch, and sync your first audience — allow 6–48 hours for match counts to populate' },
    ],
    fields: [
      { key: 'customerId', label: 'Customer ID *', placeholder: '1234567890', helper: '10 digits — omit dashes (e.g. 1234567890)' },
    ],
  },
  linkedin: {
    title: 'LinkedIn Ads', icon: '💼',
    steps: [
      { who: 'client', label: 'Grant access: go to linkedin.com/campaignmanager → select your account → Account Settings → User Management → Add User → invite info@intentflo.com → role: Account Manager → Send' },
      { who: 'client', label: 'Find your Account ID: it appears in the URL — linkedin.com/campaignmanager/accounts/XXXXXXXXX/ — copy that number' },
      { who: 'us', label: 'Connect your LinkedIn Campaign Manager account via OAuth, create your matched audience, and configure the sync in Hightouch — emails are auto-hashed, no extra action from you' },
    ],
    fields: [
      { key: 'accountId', label: 'Account ID *', placeholder: '123456789', helper: 'Found in the URL: /campaignmanager/accounts/YOUR_ID_HERE' },
    ],
  },
  tiktok: {
    title: 'TikTok Ads', icon: '🎵',
    steps: [
      { who: 'client', label: 'Grant access: in TikTok Ads Manager → Settings → Ad Account → Members → Invite Member → enter info@intentflo.com → role: Operator → Confirm' },
      { who: 'client', label: 'Find your Advertiser ID: shown in the account switcher dropdown in the top-right corner of TikTok Ads Manager' },
      { who: 'client', label: 'Find your Pixel Code (optional): Assets → Event → Manage Web Events → select your pixel → copy the Pixel ID shown at the top', optional: true },
      { who: 'us', label: 'Connect your TikTok Ads account via OAuth, configure Custom Audiences using Segment API mode in Hightouch, and push your first audience — audiences require 1,000+ users to activate' },
    ],
    fields: [
      { key: 'advertiserId', label: 'Advertiser ID *', placeholder: '7012345678901234567', helper: 'Shown in the top-right account switcher in TikTok Ads Manager' },
      { key: 'pixelId', label: 'Pixel Code', placeholder: 'ABC12DE345', helper: 'Optional — needed for web event / conversion tracking', optional: true },
    ],
  },
  twitter: {
    title: 'X (Twitter) Ads', icon: '✖️',
    steps: [
      { who: 'client', label: 'Grant access: in X Ads Manager → Settings → Account Access → Add another user → enter info@intentflo.com → role: Campaign Manager → Send invitation' },
      { who: 'client', label: 'Find your Account ID: it appears in the URL — ads.x.com/accounts/XXXXXXX — copy that ID' },
      { who: 'client', label: 'Find your Pixel ID (optional): X Business → Events Manager → Universal Website Tag → copy the Pixel ID shown at the top', optional: true },
      { who: 'us', label: 'Connect your X Ads account via OAuth, configure Custom Audience syncs in Hightouch with SHA-256 hashing, and push your first audience — no further action from you' },
    ],
    fields: [
      { key: 'accountId', label: 'Account ID *', placeholder: 'abc1234', helper: 'Found in the URL: ads.x.com/accounts/YOUR_ID_HERE' },
      { key: 'pixelId', label: 'Pixel ID', placeholder: 'nxxxxx', helper: 'Optional — from Events Manager → Universal Website Tag', optional: true },
    ],
  },
  custom: {
    title: 'Custom / Other', icon: '⚙️',
    steps: [
      { who: 'client', label: 'Describe the platform or integration you need in the notes field below — include any account names, platform links, or questions' },
      { who: 'us', label: 'Review your request, confirm feasibility, and reach out within 1 business day with a setup plan and timeline' },
    ],
    fields: [
      { key: 'note', label: 'Notes', placeholder: 'Describe the platform or integration you need, including any account references or questions…', helper: 'Any details that will help us set up your integration', multiline: true },
    ],
  },
};

const PLATFORM_DEEP_LINKS: Record<string, { label: string; url: string }[]> = {
  meta: [],
  google: [
    { label: 'Open Access & Security',    url: 'https://ads.google.com/aw/accountaccess/users' },
    { label: 'Open Customer Match Terms', url: 'https://ads.google.com/aw/accountsettings/terms' },
  ],
  linkedin: [
    { label: 'Open Campaign Manager',     url: 'https://www.linkedin.com/campaignmanager/' },
  ],
  tiktok: [
    { label: 'Open TikTok Ads Manager',   url: 'https://ads.tiktok.com/i18n/home' },
    { label: 'Open Business Center',      url: 'https://business.tiktok.com/' },
  ],
  twitter: [
    { label: 'Open X Ads Manager',        url: 'https://ads.x.com/' },
  ],
  custom: [],
};


export default function ClientOnboarding() {
  const token = new URLSearchParams(window.location.search).get('token') ?? '';

  const [phase,             setPhase]             = useState<'loading'|'select'|'form'|'done'|'error'>('loading');
  const [clientName,        setClientName]        = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [formValues,        setFormValues]        = useState<Record<string, Record<string, string>>>({});
  const [checks,            setChecks]            = useState<Record<string, boolean[]>>({});
  const [submitting,        setSubmitting]        = useState(false);
  const [errMsg,            setErrMsg]            = useState('');
  const [copiedPartnerId,   setCopiedPartnerId]   = useState(false);

  useEffect(() => {
    if (!token) { setPhase('error'); setErrMsg('No onboarding token found in this link.'); return; }
    fetch(`${API_BASE}/onboard?token=${encodeURIComponent(token)}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) { setPhase('error'); setErrMsg(d.error); return; }
        setClientName(d.clientName);
        setPhase('select');
      })
      .catch(() => { setPhase('error'); setErrMsg('Could not load your onboarding page. Please try again.'); });
  }, [token]);

  function togglePlatform(id: string) {
    setSelectedPlatforms(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  }

  function setField(platformId: string, fieldKey: string, value: string) {
    setFormValues(prev => ({
      ...prev,
      [platformId]: { ...(prev[platformId] ?? {}), [fieldKey]: value },
    }));
  }

  function toggleCheck(platformId: string, idx: number) {
    setChecks(prev => {
      const arr = [...(prev[platformId] ?? PLATFORM_SETUPS[platformId].steps.map(() => false))];
      arr[idx] = !arr[idx];
      return { ...prev, [platformId]: arr };
    });
  }

  function handleContinue() {
    if (selectedPlatforms.length === 0) return;
    const initForms: Record<string, Record<string, string>> = {};
    const initChecks: Record<string, boolean[]> = {};
    for (const pid of selectedPlatforms) {
      initForms[pid] = {};
      for (const f of PLATFORM_SETUPS[pid].fields) initForms[pid][f.key] = '';
      initChecks[pid] = PLATFORM_SETUPS[pid].steps.map(() => false);
    }
    setFormValues(initForms);
    setChecks(initChecks);
    setPhase('form');
  }

  async function handleSubmit() {
    setErrMsg('');
    for (const pid of selectedPlatforms) {
      const setup = PLATFORM_SETUPS[pid];
      for (const field of setup.fields) {
        if (!field.optional && !(formValues[pid]?.[field.key] ?? '').trim()) {
          setErrMsg(`${setup.title}: "${field.label.replace(' *', '')}" is required.`);
          return;
        }
      }
    }
    setSubmitting(true);
    try {
      const platforms: Record<string, Record<string, string>> = {};
      for (const pid of selectedPlatforms) {
        platforms[pid] = {};
        for (const f of PLATFORM_SETUPS[pid].fields) {
          const val = (formValues[pid]?.[f.key] ?? '').trim();
          if (val) platforms[pid][f.key] = val;
        }
      }
      const res = await fetch(`${API_BASE}/onboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, platforms }),
      });
      const data = await res.json();
      if (!res.ok || data.error) { setErrMsg(data.error ?? 'Submission failed. Please try again.'); return; }
      setPhase('done');
    } catch {
      setErrMsg('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Loading ──────────────────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <Page>
        <Logo />
        <div style={{ textAlign: 'center', color: '#4a5d7a', fontSize: 14 }}>
          <Spinner /> Loading your onboarding page…
        </div>
      </Page>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (phase === 'error') {
    return (
      <Page>
        <Logo />
        <div style={{
          background: '#120608', border: '1px solid #7f1d1d',
          borderRadius: 10, padding: 20, textAlign: 'center',
          color: '#fca5a5', fontSize: 14,
        }}>
          <div style={{ fontSize: 28, marginBottom: 12 }}>⚠️</div>
          {errMsg}
          <div style={{ marginTop: 12, fontSize: 12, color: '#7f1d1d' }}>
            Please contact your intentFlo account manager for a new link.
          </div>
        </div>
      </Page>
    );
  }

  // ── Done ─────────────────────────────────────────────────────────────────
  if (phase === 'done') {
    return (
      <Page>
        <Logo />
        <div style={{
          background: '#060f0a', border: '1px solid #14532d',
          borderRadius: 12, padding: 28, textAlign: 'center',
        }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>✅</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#e8edf5', marginBottom: 8 }}>
            You're all set!
          </div>
          <div style={{ fontSize: 14, color: '#64748b', lineHeight: 1.7 }}>
            Your information has been saved for{' '}
            <strong style={{ color: '#e8edf5' }}>{clientName}</strong>.
            Your IntentFlo account manager will review your access and reach out within 1 business day
            to confirm everything is connected and schedule your kickoff.
          </div>
          <div style={{ marginTop: 16, fontSize: 13, color: '#334155' }}>
            Questions? Reply to your welcome email or contact us at{' '}
            <a href="mailto:info@intentflo.com" style={{ color: '#3b82f6' }}>info@intentflo.com</a>
          </div>
        </div>
      </Page>
    );
  }

  // ── Platform Select (multi-select) ───────────────────────────────────────
  if (phase === 'select') {
    return (
      <Page>
        <Logo />
        <div style={{
          background: '#0b1220', border: '1px solid #162035',
          borderRadius: 16, padding: '28px 28px 24px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
        }}>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#e8edf5', marginBottom: 6 }}>
              Which platforms{clientName ? `, ${clientName}` : ''}?
            </div>
            <div style={{ fontSize: 13, color: '#4a5d7a', lineHeight: 1.6 }}>
              Select all the ad platforms you want IntentFlo to sync your audiences into.
              We'll give you exact setup steps for each one.
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
            {PLATFORM_OPTIONS.map(p => {
              const selected = selectedPlatforms.includes(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => togglePlatform(p.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    background: selected ? 'rgba(37,99,235,0.15)' : '#080c14',
                    border: `1px solid ${selected ? '#3b82f6' : '#1a2840'}`,
                    borderRadius: 10, padding: '11px 14px',
                    cursor: 'pointer', textAlign: 'left',
                    transition: 'border-color 0.15s, background 0.15s',
                    boxShadow: selected ? '0 0 0 3px rgba(59,130,246,0.10)' : 'none',
                  }}
                >
                  <div style={{
                    width: 20, height: 20, borderRadius: 5, flexShrink: 0,
                    background: selected ? '#3b82f6' : 'transparent',
                    border: `1.5px solid ${selected ? '#3b82f6' : '#334155'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s',
                  }}>
                    {selected && (
                      <svg width="11" height="11" viewBox="0 0 11 11">
                        <path d="M1.5 5.5L4 8L9.5 2.5" stroke="#fff" strokeWidth="1.8"
                              fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <span style={{ fontSize: 20 }}>{p.icon}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#e8edf5' }}>{p.label}</div>
                    <div style={{ fontSize: 11, color: '#475569' }}>{p.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>

          <button
            type="button"
            disabled={selectedPlatforms.length === 0}
            onClick={handleContinue}
            style={{
              width: '100%', padding: '12px 0', borderRadius: 9, border: 'none',
              background: selectedPlatforms.length === 0 ? '#111927' : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
              color: selectedPlatforms.length === 0 ? '#2a3d56' : '#fff',
              fontSize: 14, fontWeight: 600, fontFamily: 'Sora, sans-serif',
              cursor: selectedPlatforms.length === 0 ? 'not-allowed' : 'pointer',
              boxShadow: selectedPlatforms.length > 0 ? '0 4px 14px rgba(37,99,235,0.35)' : 'none',
              transition: 'background 0.15s, color 0.15s',
            }}
          >
            {selectedPlatforms.length === 0
              ? 'Select at least one platform'
              : `Continue with ${selectedPlatforms.length} platform${selectedPlatforms.length > 1 ? 's' : ''} →`
            }
          </button>
        </div>
      </Page>
    );
  }

  // ── Form (per-platform checklists) ───────────────────────────────────────
  return (
    <Page>
      <Logo />

      {/* Intro */}
      <div style={{
        background: '#0b1220', border: '1px solid #162035',
        borderRadius: 16, padding: '22px 24px 18px',
        boxShadow: '0 24px 64px rgba(0,0,0,0.5)', marginBottom: 18,
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#e8edf5', marginBottom: 5 }}>
          {clientName ? `Let's set up your platforms, ${clientName}` : 'Platform setup checklist'}
        </div>
        <div style={{ fontSize: 13, color: '#4a5d7a', lineHeight: 1.6 }}>
          For each platform below, complete the setup steps in your ad account, then enter your
          account credentials. Steps marked optional can be skipped for now.
        </div>
      </div>

      {/* One card per selected platform */}
      {selectedPlatforms.map(pid => {
        const setup = PLATFORM_SETUPS[pid];
        const pidChecks = checks[pid] ?? setup.steps.map(() => false);
        return (
          <div key={pid} style={{
            background: '#0b1220', border: '1px solid #162035',
            borderRadius: 16, padding: '22px 24px 20px',
            boxShadow: '0 24px 64px rgba(0,0,0,0.5)', marginBottom: 18,
          }}>
            {/* Platform header */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              paddingBottom: 14, marginBottom: 16, borderBottom: '1px solid #111927',
            }}>
              <span style={{ fontSize: 22 }}>{setup.icon}</span>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#e8edf5' }}>{setup.title}</div>
            </div>

            {/* Partner ID block — Meta only */}
            {pid === 'meta' && (
              <div style={{
                background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(59,130,246,0.3)',
                borderRadius: 10, padding: '14px 16px', marginBottom: 16,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#60a5fa', letterSpacing: '0.06em', marginBottom: 6 }}>
                  INTENTFLO PARTNER ID — paste this into Meta when prompted
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    flex: 1, fontFamily: 'DM Mono, monospace', fontSize: 20, fontWeight: 700,
                    color: '#e8edf5', letterSpacing: '0.04em',
                  }}>
                    908181335430042
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText('908181335430042');
                      setCopiedPartnerId(true);
                      setTimeout(() => setCopiedPartnerId(false), 2000);
                    }}
                    style={{
                      background: copiedPartnerId ? 'rgba(16,185,129,0.15)' : 'rgba(37,99,235,0.15)',
                      border: `1px solid ${copiedPartnerId ? '#10b981' : '#3b82f6'}`,
                      borderRadius: 7, padding: '6px 14px', fontSize: 12, fontWeight: 600,
                      color: copiedPartnerId ? '#10b981' : '#60a5fa',
                      cursor: 'pointer', fontFamily: 'Sora, sans-serif', flexShrink: 0,
                      transition: 'all 0.15s',
                    }}
                  >
                    {copiedPartnerId ? '✓ Copied!' : 'Copy'}
                  </button>
                </div>
                <div style={{ fontSize: 11, color: '#4a5d7a', marginTop: 8, lineHeight: 1.5 }}>
                  In Meta Business: ⚙️ Settings → Partners → Add → "Give a partner access to your assets" → paste this ID → Next
                </div>
              </div>
            )}

            {/* Step 1 — Action checklist */}
            <Step num={1} title="Setup checklist">
              {setup.steps.map((step, idx) => step.who === 'us' ? null : (
                <CheckItem
                  key={idx}
                  who={step.who}
                  checked={pidChecks[idx]}
                  onChange={() => toggleCheck(pid, idx)}
                  optional={step.optional}
                  microSteps={step.microSteps}
                  sectionLink={
                    pid === 'meta' && step.label.startsWith('Step 6') && formValues['meta']?.adAccountId
                      ? { label: 'Open Billing ↗', url: `https://business.facebook.com/billing_hub/accounts/details/?asset_id=${formValues['meta'].adAccountId}` }
                      : step.sectionLink
                  }
                >
                  {step.label}
                </CheckItem>
              ))}
              {/* Deep-link buttons */}
              {(PLATFORM_DEEP_LINKS[pid] ?? []).length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14, paddingTop: 14, borderTop: '1px dashed #101a28' }}>
                  {PLATFORM_DEEP_LINKS[pid].map(link => (
                    <a
                      key={link.url}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        background: '#0d1f3c', border: '1px solid #1e3a6e',
                        borderRadius: 7, padding: '7px 13px',
                        fontSize: 12, fontWeight: 600, color: '#60a5fa',
                        textDecoration: 'none', whiteSpace: 'nowrap',
                      }}
                    >
                      {link.label} ↗
                    </a>
                  ))}
                </div>
              )}
            </Step>

            {/* Step 2 — Credentials */}
            <Step num={2} title={`Enter your ${setup.title} credentials`}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {setup.fields.map(field => field.multiline ? (
                  <div key={field.key} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.03em' }}>
                      {field.label}
                    </label>
                    <textarea
                      value={formValues[pid]?.[field.key] ?? ''}
                      onChange={e => setField(pid, field.key, e.target.value)}
                      placeholder={field.placeholder}
                      style={{
                        width: '100%', minHeight: 90, background: '#060a10',
                        border: '1px solid #1a2840', borderRadius: 8,
                        color: '#e8edf5', fontSize: 13, fontFamily: 'Sora, sans-serif',
                        padding: '10px 13px', outline: 'none', resize: 'vertical',
                        boxSizing: 'border-box',
                      }}
                    />
                    {field.helper && (
                      <div style={{ fontSize: 11, color: '#4a5d7a', lineHeight: 1.5 }}>{field.helper}</div>
                    )}
                  </div>
                ) : (
                  <Field
                    key={field.key}
                    label={field.label}
                    value={formValues[pid]?.[field.key] ?? ''}
                    onChange={v => setField(pid, field.key, v)}
                    placeholder={field.placeholder}
                    helper={field.helper}
                    prefix={field.prefix}
                  />
                ))}
              </div>
            </Step>
          </div>
        );
      })}

      {/* Error */}
      {errMsg && (
        <div style={{
          background: '#120608', border: '1px solid #7f1d1d',
          borderRadius: 8, padding: '10px 14px',
          color: '#fca5a5', fontSize: 13, marginBottom: 16,
        }}>
          {errMsg}
        </div>
      )}

      {/* Submit */}
      <button
        type="button"
        disabled={submitting}
        onClick={handleSubmit}
        style={{
          width: '100%', padding: '13px 0', borderRadius: 9, border: 'none',
          background: submitting ? '#1e3a6e' : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
          color: '#fff', fontSize: 14, fontWeight: 600,
          fontFamily: 'Sora, sans-serif', cursor: submitting ? 'not-allowed' : 'pointer',
          boxShadow: '0 4px 14px rgba(37,99,235,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          transition: 'opacity 0.15s',
        }}
      >
        {submitting
          ? <><Spinner /> Saving…</>
          : `Submit ${selectedPlatforms.length} platform${selectedPlatforms.length > 1 ? 's' : ''} →`
        }
      </button>

      <div style={{
        marginTop: 14, fontSize: 11, color: '#2a3d56', textAlign: 'center', lineHeight: 1.6,
      }}>
        Your information is securely stored and only used by intentFlo to manage your ad campaigns.
      </div>
    </Page>
  );
}

// ─── Layout helpers ───────────────────────────────────────────────────────────

function Page({ children }: { children: ReactNode }) {
  return (
    <div style={{
      minHeight: '100vh', background: '#060a10',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '40px 20px 60px',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: '-160px', left: '-160px',
        width: 480, height: 480,
        background: 'radial-gradient(circle, rgba(37,99,235,0.15) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', bottom: '-160px', right: '-160px',
        width: 480, height: 480,
        background: 'radial-gradient(circle, rgba(124,58,237,0.12) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
      <div style={{ width: '100%', maxWidth: 540, position: 'relative', zIndex: 1 }}>
        {children}
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
         style={{ animation: 'spin 0.7s linear infinite', display: 'inline-block', verticalAlign: 'middle' }}>
      <circle cx="8" cy="8" r="6" stroke="rgba(255,255,255,0.2)" strokeWidth="2" />
      <path d="M8 2a6 6 0 0 1 6 6" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
